import { useEffect, useMemo, useState } from 'react';
import { useAuftragCtx } from './AuftragLayout.jsx';
import { useKunde, useSaveKunde } from '../hooks/data.js';
import { useToast } from '../state/toast.jsx';
import { adresse } from '../lib/format.js';
import { Icon } from '../components/Icon.jsx';
import { Stand } from '../components/Stand.jsx';

/** Vergleichbare Form eines Werts (Text getrimmt, Mehrfachauswahl als sortierte Liste). */
const norm = (f, v) => (f.typ === 'mehrfach'
  ? [...new Set((v ?? []).map(String))].sort().join('\u0001')
  : String(v ?? '').replace(/\r\n?/g, '\n').trim());

const useOnline = () => {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
};

/**
 * Kundeninfos: Kontakt des Exxas-Kunden und seine Zusatzfelder (Bezeichnung, Typ und Auswahl aus Exxas).
 * Geladen erst beim Öffnen. Gespeichert werden nur geänderte Felder, jeweils mit dem Wert beim Öffnen –
 * hat jemand anderes inzwischen in Exxas geändert, meldet die API einen Konflikt statt zu überschreiben.
 */
export function Kunde() {
  const { nr } = useAuftragCtx();
  const kunde = useKunde(nr);
  const save = useSaveKunde(nr);
  const toast = useToast();
  const online = useOnline();
  /** Entwürfe: feld → { wert, basis } (basis = Exxas-Wert, auf dem die Änderung beruht). */
  const [entwurf, setEntwurf] = useState({});
  const [error, setError] = useState(null);

  const k = kunde.data;
  const felder = useMemo(() => k?.felder ?? [], [k]);
  const byFeld = useMemo(() => new Map(felder.map((f) => [f.feld, f])), [felder]);

  // Nach dem Neuladen: Entwürfe verwerfen, deren Feld sich in Exxas inzwischen geändert hat
  useEffect(() => {
    setEntwurf((alt) => {
      const next = Object.fromEntries(Object.entries(alt).filter(([feld, e]) => {
        const f = byFeld.get(feld);
        return f && norm(f, f.wert) === norm(f, e.basis) && norm(f, e.wert) !== norm(f, f.wert);
      }));
      return Object.keys(next).length === Object.keys(alt).length ? alt : next;
    });
  }, [byFeld]);

  const wertVon = (f) => (f.feld in entwurf ? entwurf[f.feld].wert : f.wert);
  const setWert = (f, wert) => {
    setError(null);
    setEntwurf((alt) => {
      const basis = alt[f.feld]?.basis ?? f.wert;
      if (norm(f, wert) === norm(f, basis)) { const { [f.feld]: _, ...rest } = alt; return rest; }
      return { ...alt, [f.feld]: { wert, basis } };
    });
  };
  const anzahl = Object.keys(entwurf).length;

  const speichern = async () => {
    setError(null);
    const aenderungen = Object.fromEntries(Object.entries(entwurf).map(([feld, e]) => [feld, { alt: e.basis, neu: e.wert }]));
    try {
      const res = await save.mutateAsync(aenderungen);
      setEntwurf({});
      toast({ text: res.geaendert?.length ? 'Kundeninfos in Exxas gespeichert' : 'Keine Änderungen – stand schon so in Exxas' });
    } catch (err) {
      if (err.code === 'KONFLIKT') kunde.refetch();
      setError(err.message);
    }
  };

  if (kunde.isLoading) return <div className="narrow"><div className="skeleton" style={{ height: 120 }} /><div className="skeleton" style={{ height: 320 }} /></div>;
  if (kunde.isError && !k) {
    return (
      <div className="narrow">
        <div className="empty" role="alert">
          {kunde.error.message}
          <button className="btn" onClick={() => kunde.refetch()}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  const adr = adresse(k);
  return (
    <div className="narrow">
      <Stand abfragen={[kunde]} />
      <section className="card">
        <div className="stack">
          <span className="eyebrow" style={{ color: 'var(--muted-2)' }}>Kunde{k.nummer ? ` ${k.nummer}` : ''}</span>
          <span style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.25 }}>{k.name || '(ohne Namen)'}</span>
          {k.zusatz && <span className="sub" style={{ fontSize: 14 }}>{k.zusatz}</span>}
          {adr && <span className="sub" style={{ fontSize: 14 }}>{adr}</span>}
        </div>
        {(k.telefon || k.mobile || k.email) && (
          <div className="wrapchips">
            {k.telefon && <a className="chip" href={`tel:${k.telefon.replace(/[^\d+]/g, '')}`}><Icon name="phone" size={16} />{k.telefon}</a>}
            {k.mobile && <a className="chip" href={`tel:${k.mobile.replace(/[^\d+]/g, '')}`}><Icon name="phone" size={16} />{k.mobile}</a>}
            {k.email && <a className="chip" href={`mailto:${k.email}`}><Icon name="mail" size={16} />{k.email}</a>}
          </div>
        )}
        {k.hinweis && <div className="notice" role="note"><Icon name="warn" size={18} /><span style={{ whiteSpace: 'pre-line' }}>{k.hinweis}</span></div>}
        {k.bemerkungen && <p className="sub" style={{ whiteSpace: 'pre-line', lineHeight: 1.45 }}>{k.bemerkungen}</p>}
      </section>

      {!online && <div className="notice info" role="status"><Icon name="warn" size={18} />Offline – du siehst den zuletzt geladenen Stand. Speichern geht wieder mit Verbindung.</div>}
      {kunde.isError && k && online && <div className="notice err" role="alert"><Icon name="warn" size={18} />Aktueller Stand konnte nicht geladen werden: {kunde.error.message}</div>}

      <section className="card">
        <h2 className="h2">Anlage &amp; Zusatzinfos</h2>
        {felder.length === 0 && <p className="sub">Für Kunden sind in Exxas keine Zusatzfelder eingerichtet.</p>}
        <div className="kgrid">
          {felder.map((f) => (
            <Feld key={f.feld} f={f} wert={wertVon(f)} geaendert={f.feld in entwurf} onChange={(v) => setWert(f, v)} disabled={save.isPending} />
          ))}
        </div>
      </section>

      {(anzahl > 0 || error) && (
        <div className="savebar" role="region" aria-label="Änderungen speichern">
          {error && <div className="notice err" role="alert"><Icon name="warn" size={18} />{error}</div>}
          {anzahl > 0 && (
            <div className="row">
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700 }}>{anzahl} {anzahl === 1 ? 'Änderung' : 'Änderungen'}</span>
              <button className="btn secondary" style={{ padding: '0 14px' }} onClick={() => { setEntwurf({}); setError(null); }} disabled={save.isPending}>Verwerfen</button>
              <button className="btn" style={{ padding: '0 16px' }} onClick={speichern} disabled={save.isPending || !online} title="Änderungen in Exxas speichern">{save.isPending ? 'Speichert …' : 'Speichern'}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Feld({ f, wert, geaendert, onChange, disabled }) {
  const id = `kf-${f.feld}`;
  const label = (
    <span className="label row" style={{ gap: 6 }}>
      {f.label}{f.pflicht ? ' *' : ''}
      {geaendert && <span className="pill warn">geändert</span>}
    </span>
  );

  if (f.typ === 'mehrfach') {
    const gewaehlt = new Set(wert);
    // auch Werte zeigen, die (nicht mehr) in der Auswahl stehen
    const optionen = [...f.optionen, ...wert.filter((w) => !f.optionen.includes(w))];
    const toggle = (o) => onChange(gewaehlt.has(o) ? wert.filter((w) => w !== o) : [...wert, o]);
    return (
      <fieldset className="field kfull" style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ padding: 0, marginBottom: 6 }}>{label}</legend>
        {f.hinweis && <span className="sub">{f.hinweis}</span>}
        <div className="wrapchips">
          {optionen.map((o) => (
            <button key={o} type="button" className={`chip${gewaehlt.has(o) ? ' on' : ''}`} aria-pressed={gewaehlt.has(o)} onClick={() => toggle(o)} disabled={disabled}>
              {gewaehlt.has(o) && <Icon name="check" size={14} stroke={3} />}{o}
            </button>
          ))}
        </div>
        <span className="sub">{wert.length ? `${wert.length} gewählt` : 'Mehrfachauswahl möglich'}</span>
      </fieldset>
    );
  }

  if (f.typ === 'auswahl' && !f.frei) {
    const optionen = wert && !f.optionen.includes(wert) ? [...f.optionen, wert] : f.optionen;
    return (
      <label className="field" htmlFor={id}>
        {label}
        <select id={id} className="input" value={wert} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
          <option value="">– keine Angabe –</option>
          {optionen.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {f.hinweis && <span className="sub">{f.hinweis}</span>}
      </label>
    );
  }

  const max = f.maxZeichen ?? 2000;
  return (
    <label className={`field${f.typ === 'text' && String(wert).length > 80 ? ' kfull' : ''}`} htmlFor={id}>
      {label}
      {f.typ === 'zeile' || f.typ === 'auswahl' ? (
        <>
          <input id={id} className="input" value={wert} onChange={(e) => onChange(e.target.value)} maxLength={max} placeholder={f.hinweis ?? ''} disabled={disabled}
            list={f.typ === 'auswahl' ? `${id}-liste` : undefined} />
          {f.typ === 'auswahl' && <datalist id={`${id}-liste`}>{f.optionen.map((o) => <option key={o} value={o} />)}</datalist>}
        </>
      ) : (
        <textarea id={id} className="input" style={{ minHeight: 50 }} rows={Math.min(6, Math.max(1, String(wert).split('\n').length))}
          value={wert} onChange={(e) => onChange(e.target.value)} maxLength={max} placeholder={f.hinweis ?? ''} disabled={disabled} />
      )}
      {f.hinweis && wert && <span className="sub">{f.hinweis}</span>}
    </label>
  );
}
